/**
 * Janko Piano - UI Module
 * Handles modals, settings, toasts, and UI state management
 * @version 0.2.63
 */

import { CONFIG, CHORD_PATTERNS, INSTRUMENT_IDS, INSTRUMENT_NAMES, INSTRUMENT_ICONS, INSTRUMENT_COLORS, COLOR_FILTERS } from './config.js';
import { getCurrentInstrument, setInstrument, getSynth, setDistortionAmount, setEchoAmount } from './audio.js';
import { generateKeys, render, requestRender, updateShapeCache } from './renderer.js';

// =============================================================================
// TOAST NOTIFICATIONS
// =============================================================================

export function showToast(message) {
    const t = document.getElementById('toast');
    if (t) {
        t.innerText = message;
        t.classList.add('visible');
        setTimeout(() => t.classList.remove('visible'), 2000);
    }
}

// =============================================================================
// MODAL MANAGEMENT
// =============================================================================

export function getVisibleModal() {
    if (document.getElementById('instrument-modal')?.classList.contains('visible')) return 'instrument';
    const quickModal = document.getElementById('quick-settings-modal');
    if (quickModal?.classList.contains('visible') || quickModal?.style.display === 'flex') return 'quick-settings';
    if (document.getElementById('info-modal')?.classList.contains('visible')) return 'info';
    if (document.getElementById('settings-modal')?.classList.contains('visible')) return 'settings';
    return null;
}

export function toggleSettings(e) {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const modal = document.getElementById('settings-modal');
    modal.classList.toggle('visible');
    if (window.updateAdLayout) window.updateAdLayout();
}

export function openSettings() {
    document.getElementById('settings-modal').classList.add('visible');
    if (window.updateAdLayout) window.updateAdLayout();
}

export function closeSettings() {
    document.getElementById('settings-modal').classList.remove('visible');
    if (window.updateAdLayout) window.updateAdLayout();
}

export function toggleInfo() {
    const infoModal = document.getElementById('info-modal');
    const isClosing = infoModal.classList.contains('visible');
    
    // If closing the modal, save the checkbox preference
    if (isClosing) {
        const checkbox = document.getElementById('dont-show-info-again');
        if (checkbox) {
            if (checkbox.checked) localStorage.setItem('hideInfoOnStartup', 'true');
            else localStorage.removeItem('hideInfoOnStartup');
        }
    }
    
    infoModal.classList.toggle('visible');
    if (window.updateAdLayout) window.updateAdLayout();
}

export function openQuickSettings() {
    const infoModal = document.getElementById('info-modal');
    
    // Save info modal checkbox preference when transitioning to quick settings
    const infoCheckbox = document.getElementById('dont-show-info-again');
    if (infoCheckbox) {
        if (infoCheckbox.checked) localStorage.setItem('hideInfoOnStartup', 'true');
        else localStorage.removeItem('hideInfoOnStartup');
    }
    
    infoModal.classList.remove('visible');
    document.getElementById('quick-settings-modal').classList.add('visible');
    if (window.updateAdLayout) window.updateAdLayout();
}

export function closeQuickSettings() {
    // Save checkbox preference when closing quick settings
    const checkbox = document.getElementById('dont-show-quicksettings-again');
    if (checkbox) {
        if (checkbox.checked) localStorage.setItem('hideQuickSettingsOnStartup', 'true');
        else localStorage.removeItem('hideQuickSettingsOnStartup');
    }
    
    document.getElementById('quick-settings-modal').classList.remove('visible');
    if (window.updateAdLayout) window.updateAdLayout();
}

export function closeModalOnClick(e) {
    if (e.target.classList.contains('modal-overlay')) {
        e.target.classList.remove('visible');
    }
}

// =============================================================================
// INSTRUMENT GRID
// =============================================================================

let selectedInstIndex = 0;

export function openInstrumentGrid() {
    const container = document.getElementById('inst-grid-container');
    container.innerHTML = '';
    if (window.updateAdLayout) window.updateAdLayout();

    const currentInstId = getCurrentInstrument();
    selectedInstIndex = INSTRUMENT_IDS.indexOf(currentInstId);
    if (selectedInstIndex === -1) selectedInstIndex = 0;

    INSTRUMENT_IDS.forEach((id, idx) => {
        const div = document.createElement('button');
        div.type = 'button';
        div.className = 'instrument-btn ' + (currentInstId === id ? 'active selected' : '');
        div.id = 'instrument-btn-' + idx;
        div.onclick = () => {
            setInst(id);
            closeInstrumentGrid();
        };

        const iconDiv = document.createElement('div');
        iconDiv.className = 'inst-icon';
        const img = document.createElement('img');
        const iconName = INSTRUMENT_ICONS[id] || id;
        img.src = `icons/${iconName}.svg`;
        const color = INSTRUMENT_COLORS[id] || '#FF3030';
        img.style.filter = COLOR_FILTERS[color] || COLOR_FILTERS['#FF3030'];
        iconDiv.appendChild(img);

        const name = document.createElement('div');
        name.className = 'inst-name';
        name.innerText = INSTRUMENT_NAMES[id] || id;

        div.appendChild(iconDiv);
        div.appendChild(name);
        container.appendChild(div);
    });

    document.getElementById('instrument-modal').classList.add('visible');
}

export function closeInstrumentGrid() {
    const modal = document.getElementById('instrument-modal');
    if (modal) modal.classList.remove('visible');
    if (window.updateAdLayout) window.updateAdLayout();
}

export function updateModalSelection() {
    const cards = document.querySelectorAll('.instrument-btn');
    cards.forEach(c => c.classList.remove('selected'));

    const currentEl = document.getElementById('instrument-btn-' + selectedInstIndex);
    if (currentEl) {
        currentEl.classList.add('selected');
        currentEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
}

export function getSelectedInstIndex() {
    return selectedInstIndex;
}

export function setSelectedInstIndex(idx) {
    selectedInstIndex = idx;
}

// =============================================================================
// INSTRUMENT SELECTION
// =============================================================================

export function updateHudIcon(id) {
    const hudIcon = document.getElementById('hud-btn-icon');
    if (hudIcon) {
        const color = INSTRUMENT_COLORS[id] || '#FF9F0A';
        const iconName = INSTRUMENT_ICONS[id] || id;
        hudIcon.src = `icons/${iconName}.svg`;
        hudIcon.style.filter = COLOR_FILTERS[color] || COLOR_FILTERS['#FF3030'];
    }
}

export function setInst(id, updateMinimapFn) {
    setInstrument(id);

    const color = INSTRUMENT_COLORS[id] || '#FF9F0A';
    CONFIG.colors.active = color;
    document.documentElement.style.setProperty('--accent', color);

    updateShapeCache();

    // Update HUD Icon
    updateHudIcon(id);

    if (document.getElementById('inst-select')) {
        document.getElementById('inst-select').blur();
    }
    
    showToast(id.toUpperCase().replace('_', ' '));
    requestRender(id);
}

export function cycleInst(d, updateMinimapFn) {
    const currentInstId = getCurrentInstrument();
    let idx = INSTRUMENT_IDS.indexOf(currentInstId) + d;
    if (idx < 0) idx = INSTRUMENT_IDS.length - 1;
    if (idx >= INSTRUMENT_IDS.length) idx = 0;
    
    const select = document.getElementById('inst-select');
    if (select) select.value = INSTRUMENT_IDS[idx];
    
    setInst(INSTRUMENT_IDS[idx], updateMinimapFn);
}

// =============================================================================
// SETTINGS CONTROLS
// =============================================================================

export let minimapDirty = true;
let scrollGenReq = null;

export function setMinimapDirty(value) {
    minimapDirty = value;
}

export function setChord(mode, silent = false) {
    CONFIG.chordMode = mode;
    if (!silent) {
        showToast(mode.toUpperCase());
    }
    const select = document.getElementById('chord-select');
    if (select) {
        select.value = mode;
        select.blur();
    }
}

export function setRows(v, updateMinimapFn) {
    let rows = parseInt(v);
    // Clamp to maxRows for low-end devices
    if (rows > CONFIG.maxRows) rows = CONFIG.maxRows;
    CONFIG.rows = rows;
    minimapDirty = true;
    const valEl = document.getElementById('rows-val');
    if (valEl) valEl.innerText = rows;
    const slider = document.getElementById('rows-slider');
    if (slider) {
        slider.value = rows;
        slider.max = CONFIG.maxRows;  // Update slider max
    }
    generateKeys(updateMinimapFn);
}

export function setWidth(v, updateMinimapFn) {
    CONFIG.zoom = parseFloat(v);
    const valEl = document.getElementById('zoom-val');
    if (valEl) valEl.innerText = "x" + CONFIG.zoom.toFixed(1);
    generateKeys(updateMinimapFn);
    requestRender(getCurrentInstrument());
}

export function setKeyShape(v, updateMinimapFn) {
    CONFIG.keyShape = v;
    minimapDirty = true;
    
    const slider = document.getElementById('zoom-slider');
    const label = document.getElementById('zoom-label');
    if (slider) {
        slider.disabled = false;
        slider.style.opacity = '1';
        slider.style.pointerEvents = 'auto';
    }
    if (label) {
        label.style.opacity = '1';
    }
    
    generateKeys(updateMinimapFn);
    requestRender(getCurrentInstrument());
}

export function setColorScheme(v, updateMinimapFn) {
    CONFIG.colorScheme = v;
    minimapDirty = true;
    generateKeys(updateMinimapFn);
    updateShapeCache();
    requestRender(getCurrentInstrument());
    showToast("Color: " + v.toUpperCase());
}

export function setReverb(v) {
    CONFIG.reverbAmt = parseFloat(v);
    const valEl = document.getElementById('reverb-val');
    if (valEl) valEl.innerText = Math.round(v * 100) + "%";
    
    // Apply to SpessaSynth
    const synth = getSynth();
    if (synth && synth.setReverbLevel) {
        synth.setReverbLevel(parseFloat(v));
    } else if (synth && synth.controllerChange) {
        // Use MIDI CC for reverb (CC 91)
        synth.controllerChange(0, 91, Math.round(parseFloat(v) * 127));
    }
    
    // Native Hook
    if (window.nativePlugin) {
        window.nativePlugin.setReverb({ roomSize: 0.6, damping: 0.5, width: 0.5, level: parseFloat(v) });
    }
}

export function setChorus(v) {
    CONFIG.chorusAmt = parseFloat(v);
    const valEl = document.getElementById('chorus-val');
    if (valEl) valEl.innerText = Math.round(v * 100) + "%";
    
    // Apply to SpessaSynth
    const synth = getSynth();
    if (synth && synth.setChorusLevel) {
        synth.setChorusLevel(parseFloat(v));
    } else if (synth && synth.controllerChange) {
        // Use MIDI CC for chorus (CC 93)
        synth.controllerChange(0, 93, Math.round(parseFloat(v) * 127));
    }
    
    // Native Hook
    if (window.nativePlugin) {
        window.nativePlugin.setChorus({ amount: parseFloat(v) });
    }
}

export function setDistortion(v) {
    CONFIG.distortionAmt = parseFloat(v);
    const valEl = document.getElementById('distortion-val');
    if (valEl) valEl.innerText = Math.round(v * 100) + "%";
    
    // Apply Web Audio API distortion
    setDistortionAmount(parseFloat(v));
    
    // Native Hook
    if (window.nativePlugin) {
        window.nativePlugin.setDistortion({ amount: parseFloat(v) });
    }
}

export function setEcho(v) {
    CONFIG.echoAmt = parseFloat(v);
    const valEl = document.getElementById('echo-val');
    if (valEl) valEl.innerText = Math.round(v * 100) + "%";
    
    // Apply Web Audio API echo/delay
    setEchoAmount(parseFloat(v));
    
    // Native Hook
    if (window.nativePlugin) {
        window.nativePlugin.setEcho({ amount: parseFloat(v) });
    }
}

export function setScroll(v, updateMinimapFn) {
    let val = parseFloat(v);
    if (val < 0) val = 0;
    
    const sl = document.getElementById('scroll-slider');
    if (sl && val > sl.max) val = sl.max;
    if (sl) sl.value = val;
    
    CONFIG.scrollPos = val;
    // Update minimap thumb immediately, then defer heavy layout work so the UI can paint.
    if (updateMinimapFn) updateMinimapFn(sl ? sl.max : 5.2);
    
    let lbl = "Mid";
    if (val < 1) lbl = "Sub-Bass";
    else if (val < 2) lbl = "Bass";
    else if (val < 3) lbl = "Tenor";
    else if (val < 4) lbl = "Alto";
    else if (val < 5) lbl = "Soprano";
    else lbl = "Treble";
    
    const rangeVal = document.getElementById('range-val');
    if (rangeVal) rangeVal.innerText = lbl;
    
    if (!scrollGenReq) {
        scrollGenReq = requestAnimationFrame(() => {
            scrollGenReq = null;
            generateKeys(updateMinimapFn);
        });
    }
}

// =============================================================================
// SPLIT OCTAVE
// =============================================================================

export function applySplitOctave(state, updateMinimapFn) {
    CONFIG.splitOctave = state;
    minimapDirty = true;
    
    const b = document.getElementById('btn-split-octave');
    if (b) {
        const svg = b.querySelector('svg');
        if (svg) {
            svg.innerHTML = CONFIG.splitOctave 
                ? '<path d="M4 4h4v4H4z M10 4h4v4h-4z M16 4h4v4h-4z"/><rect x="2" y="11" width="20" height="2" fill="currentColor" rx="1"/><path d="M7 16h4v4H7z M13 16h4v4h-4z" opacity="0.5"/>' 
                : '<path d="M4 6h4v4H4z M10 6h4v4h-4z M16 6h4v4h-4z"/><path d="M7 12h4v4H7z M13 12h4v4h-4z" opacity="0.7"/>';
        }
        b.classList.toggle('active', CONFIG.splitOctave);
    }
    
    generateKeys(updateMinimapFn);
    requestRender(getCurrentInstrument());
}

export function toggleSplitOctave(updateMinimapFn) {
    applySplitOctave(!CONFIG.splitOctave, updateMinimapFn);
    showToast("Split: " + (CONFIG.splitOctave ? "ON" : "OFF"));
}

export function toggleKeyboardLayout() {
    CONFIG.keyboardVisuals = !CONFIG.keyboardVisuals;
    const btn = document.getElementById('btn-kb-layout');
    if (btn) btn.classList.toggle('active');
    requestRender(getCurrentInstrument());
}

// =============================================================================
// QUICK SETUP HELPERS
// =============================================================================

export function setQuickSplit(isSplit, updateMinimapFn) {
    applySplitOctave(isSplit, updateMinimapFn);
    const bo = document.getElementById('btn-qs-orig');
    const bs = document.getElementById('btn-qs-split');
    if (bo) bo.classList.toggle('active', !isSplit);
    if (bs) bs.classList.toggle('active', isSplit);
    
    // Save to localStorage
    import('./settings.js').then(({ saveSplitOctave }) => saveSplitOctave(isSplit));
}

export function setQuickKeyShape(shape, updateMinimapFn) {
    setKeyShape(shape, updateMinimapFn);
    
    const select = document.getElementById('keyshape-select');
    if (select) select.value = shape;

    // Update quick settings buttons
    ['original', 'diamond', 'hexagon', 'circle'].forEach(s => {
        const btn = document.getElementById('btn-qs-shape-' + s);
        if (btn) btn.classList.toggle('active', s === shape);
    });
    
    // Update settings panel buttons
    ['original', 'square', 'hexagon', 'circle', 'diamond'].forEach(s => {
        const btn = document.getElementById('btn-shape-' + s);
        if (btn) btn.classList.toggle('active', s === shape);
    });

    const preview = document.getElementById('qs-chord-preview');
    if (preview) {
        preview.classList.remove('preview-shape-circle', 'preview-shape-square', 'preview-shape-hexagon');
        if (shape !== 'original') {
            preview.classList.add('preview-shape-' + shape);
        }
    }
    
    // Save to localStorage
    import('./settings.js').then(({ saveKeyShape }) => saveKeyShape(shape));
}

export function setQuickRows(rows, updateMinimapFn) {
    setRows(rows, updateMinimapFn);
    ['4', '6', '8', '12'].forEach(r => {
        const el = document.getElementById('btn-qs-r' + r);
        if (el) el.classList.remove('active');
    });
    const activeEl = document.getElementById('btn-qs-r' + rows);
    if (activeEl) activeEl.classList.add('active');
    
    // Save to localStorage
    import('./settings.js').then(({ saveRows }) => saveRows(rows));
}

export function setQuickChord(mode, silent = false) {
    setChord(mode, silent);
    const map = { 'off': 'coff', 'major': 'cmaj', 'minor': 'cmin', '7th': 'c7', 'sus2': 'csus2', 'sus4': 'csus4', 'aug': 'caug', 'dim': 'cdim', 'power': 'cpow' };
    
    Object.values(map).forEach(id => {
        const el = document.getElementById('btn-qs-' + id);
        if (el) el.classList.remove('active');
    });
    
    const activeEl = document.getElementById('btn-qs-' + map[mode]);
    if (activeEl) activeEl.classList.add('active');

    const preview = document.getElementById('qs-chord-preview');
    if (preview) preview.innerHTML = CHORD_PATTERNS[mode] || "";
}

// =============================================================================
// FULLSCREEN
// =============================================================================

export async function toggleFullscreen(updateMinimapFn) {
    // Check if native platform (Android/iOS)
    const isNative = window.Capacitor && window.Capacitor.isNativePlatform();
    
    if (isNative) {
        // Native fullscreen using StatusBar plugin
        const StatusBar = window.Capacitor?.Plugins?.StatusBar;
        if (StatusBar) {
            try {
                // Toggle fullscreen state
                const isFullscreen = CONFIG.isFullscreen || false;
                
                if (!isFullscreen) {
                    // Enter fullscreen - hide status bar
                    await StatusBar.hide();
                    CONFIG.isFullscreen = true;
                    localStorage.setItem('fullscreenState', 'true');
                    showToast("Fullscreen: ON");
                } else {
                    // Exit fullscreen - show status bar
                    await StatusBar.show();
                    CONFIG.isFullscreen = false;
                    localStorage.setItem('fullscreenState', 'false');
                    showToast("Fullscreen: OFF");
                }
            } catch (err) {
                console.error("Native fullscreen error:", err);
                showToast("Fullscreen failed");
            }
        }
    } else {
        // Web fullscreen using standard API
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().then(() => {
                CONFIG.isFullscreen = true;
                localStorage.setItem('fullscreenState', 'true');
                showToast("Fullscreen: ON");
            }).catch(err => {
                console.error("Error attempting to enable fullscreen:", err);
                showToast("Fullscreen failed");
            });
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen().then(() => {
                    CONFIG.isFullscreen = false;
                    localStorage.setItem('fullscreenState', 'false');
                    showToast("Fullscreen: OFF");
                });
            }
        }
    }
}

// =============================================================================
// PREMIUM / PURCHASE
// =============================================================================

export function buyPremium() {
    CONFIG.isPaidUser = true;
    if (window.updateAdLayout) window.updateAdLayout();
    showToast("Purchase Successful! Ads Removed.");
}

export function restorePurchase() {
    CONFIG.isPaidUser = true;
    if (window.updateAdLayout) window.updateAdLayout();
    showToast("Purchases Restored.");
}

// =============================================================================
// ORIENTATION LOCK (Native only)
// =============================================================================

export function toggleOrientationLock() {
    // Handled by native Capacitor plugin
    if (window.Capacitor && window.Capacitor.Plugins.ScreenOrientation) {
        // Toggle logic would go here
        showToast("Orientation Lock Toggled");
    }
}

// =============================================================================
// DEVELOPER TOOLS
// =============================================================================

export function togglePayStatus() {
    CONFIG.isPaidUser = !CONFIG.isPaidUser;
    const btn = document.getElementById('btn-pay-toggle');
    if (btn) {
        btn.innerHTML = CONFIG.isPaidUser ? "Current: PREMIUM (No Ads)" : "Current: Free (Ads ON)";
    }
    showToast(CONFIG.isPaidUser ? "Simulating Premium User" : "Simulating Free User");
    if (window.updateAdLayout) window.updateAdLayout();
}

export function simulateDevice(type, autoDetectFn, updateMinimapFn) {
    CONFIG.simulatedType = type;
    showToast("Simulating: " + type);

    if (type === 'auto') {
        if (autoDetectFn) autoDetectFn();
    } else {
        CONFIG.detectedType = (type === 'mobile_web') ? 'mobile_web' : (type === 'desktop' ? 'desktop' : 'mobile_app');
    }

    let recRows = (CONFIG.detectedType === 'desktop') ? 6 : 4;
    setQuickRows(recRows, updateMinimapFn);

    if (window.updateAdLayout) window.updateAdLayout();
}

// =============================================================================
// NAVIGATION HELPERS
// =============================================================================

export function moveFocusInContainer(containerId, dir, selector = 'button, input, select') {
    const container = document.getElementById(containerId);
    if (!container) return;
    
    const elements = Array.from(container.querySelectorAll(selector))
        .filter(el => el.offsetParent !== null && !el.disabled);

    let current = document.activeElement;
    let idx = elements.indexOf(current);

    if (idx === -1) {
        idx = dir > 0 ? 0 : elements.length - 1;
    } else {
        idx = (idx + dir + elements.length) % elements.length;
    }
    if (elements[idx]) elements[idx].focus();
}

export function findNextVisualElement(containerId, currentEl, direction, selector) {
    const container = document.getElementById(containerId);
    if (!container || !currentEl) return null;
    
    const elements = Array.from(container.querySelectorAll(selector))
        .filter(el => el.offsetParent !== null && !el.disabled);

    const curRect = currentEl.getBoundingClientRect();
    const curCenter = { x: curRect.left + curRect.width / 2, y: curRect.top + curRect.height / 2 };

    let bestParams = { el: null, dist: Infinity };

    elements.forEach(el => {
        if (el === currentEl) return;
        const rect = el.getBoundingClientRect();
        const center = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };

        let isCandidate = false;
        const sensitivity = 5;

        if (direction === 'ArrowUp') isCandidate = center.y < curCenter.y - sensitivity;
        else if (direction === 'ArrowDown') isCandidate = center.y > curCenter.y + sensitivity;
        else if (direction === 'ArrowLeft') isCandidate = center.x < curCenter.x - sensitivity;
        else if (direction === 'ArrowRight') isCandidate = center.x > curCenter.x + sensitivity;

        if (isCandidate) {
            const dx = center.x - curCenter.x;
            const dy = center.y - curCenter.y;
            const distSq = dx * dx + dy * dy;

            let penalty = 0;
            if (direction === 'ArrowUp' || direction === 'ArrowDown') penalty = Math.abs(dx) * 20;
            else penalty = Math.abs(dy) * 20;

            const score = distSq + (penalty * penalty);

            if (score < bestParams.dist) {
                bestParams = { el: el, dist: score };
            }
        }
    });
    return bestParams.el;
}

// =============================================================================
// MIDI UI FUNCTIONS
// =============================================================================

export function updateMIDIDeviceUI() {
    const inputSelect = document.getElementById('midi-input-select');
    const outputSelect = document.getElementById('midi-output-select');
    
    if (!inputSelect || !outputSelect) return;
    
    // Get devices from MIDI module
    const inputDevices = window.getInputDevices ? window.getInputDevices() : [];
    const outputDevices = window.getOutputDevices ? window.getOutputDevices() : [];
    
    // Clear existing options (except "None")
    inputSelect.innerHTML = '<option value="">None (Disabled)</option>';
    outputSelect.innerHTML = '<option value="">None (Disabled)</option>';
    
    // Populate input devices
    inputDevices.forEach(device => {
        const option = document.createElement('option');
        option.value = device.id;
        option.textContent = `${device.name}`;
        if (device.manufacturer && device.manufacturer !== 'Unknown') {
            option.textContent += ` (${device.manufacturer})`;
        }
        inputSelect.appendChild(option);
    });
    
    // Populate output devices
    outputDevices.forEach(device => {
        const option = document.createElement('option');
        option.value = device.id;
        option.textContent = `${device.name}`;
        if (device.manufacturer && device.manufacturer !== 'Unknown') {
            option.textContent += ` (${device.manufacturer})`;
        }
        outputSelect.appendChild(option);
    });
    
    // Restore selected devices
    if (CONFIG.midiInputDeviceId) {
        inputSelect.value = CONFIG.midiInputDeviceId;
    }
    if (CONFIG.midiOutputDeviceId) {
        outputSelect.value = CONFIG.midiOutputDeviceId;
    }
    
    // Update status indicators
    updateMIDIStatusIndicators();
}

export function updateMIDIStatusIndicators() {
    const midiDot = document.getElementById('midi-status-dot');
    
    if (!midiDot) return;
    
    const selectedInputDevice = window.getSelectedInputDevice ? window.getSelectedInputDevice() : null;
    const selectedOutputDevice = window.getSelectedOutputDevice ? window.getSelectedOutputDevice() : null;
    
    // Show green dot if either input or output is connected
    if ((CONFIG.midiInputEnabled && selectedInputDevice) || (CONFIG.midiOutputEnabled && selectedOutputDevice)) {
        midiDot.style.display = 'block';
    } else {
        midiDot.style.display = 'none';
    }
}

// Expose MIDI UI functions globally
window.updateMIDIDeviceUI = updateMIDIDeviceUI;
window.updateMIDIStatusIndicators = updateMIDIStatusIndicators;

// =============================================================================
// SLIDER INTERACTION - HIDE SETTINGS DURING DRAG
// =============================================================================

export function initSliderInteraction() {
    const rowsSlider = document.getElementById('rows-slider');
    const settingsModal = document.getElementById('settings-modal');
    
    if (!rowsSlider || !settingsModal) return;
    
    const fadeOut = () => {
        settingsModal.classList.add('slider-dragging');
    };
    
    const fadeIn = () => {
        settingsModal.classList.remove('slider-dragging');
    };
    
    // Mouse events
    rowsSlider.addEventListener('mousedown', fadeOut);
    document.addEventListener('mouseup', fadeIn);
    
    // Touch events
    rowsSlider.addEventListener('touchstart', fadeOut);
    document.addEventListener('touchend', fadeIn);
}

window.initSliderInteraction = initSliderInteraction;
